import { useState, useCallback, useEffect } from "react";

// Tri-mode selection: "exclude" = all on except keys; "include" = only keys on.
export function useRowSelection() {
  const [sel, setSel] = useState({ mode: "exclude", keys: new Set() });
  const isOn = useCallback((k) => (sel.mode === "exclude" ? !sel.keys.has(k) : sel.keys.has(k)), [sel]);
  const toggle = (k) =>
    setSel((s) => {
      const keys = new Set(s.keys);
      keys.has(k) ? keys.delete(k) : keys.add(k);
      return { ...s, keys };
    });
  const setAll = (on) => setSel({ mode: on ? "exclude" : "include", keys: new Set() });
  const reset = () => setAll(true);
  return { isOn, toggle, setAll, reset };
}

// True when none of the promo's weekdays (0=Mon) fall inside its date range.
export function promoNeverRuns(p) {
  if (!p.start_date || !p.end_date || !p.days_of_week?.length) return true;
  const [ys, ms, ds] = p.start_date.split("-").map(Number);
  const [ye, me, de] = p.end_date.split("-").map(Number);
  const end = new Date(ye, me - 1, de);
  for (let d = new Date(ys, ms - 1, ds), i = 0; d <= end && i < 7; d.setDate(d.getDate() + 1), i++) {
    if (p.days_of_week.includes((d.getDay() + 6) % 7)) return false;
  }
  return true;
}

export const NeverRunsWarning = ({ p }) =>
  promoNeverRuns(p) ? (
    <p className="text-xs text-amber-400" data-testid={`promo-never-runs-${p.id}`}>
      Will never run: none of the chosen days fall between {p.start_date} and {p.end_date}. Edit the days or dates.
    </p>
  ) : null;

const listNames = (sites) => (sites.length > 3 ? `${sites.slice(0, 3).join(", ")} and ${sites.length - 3} more` : sites.join(", "));

export const FailedSitesNotice = ({ sites }) =>
  sites?.length ? (
    <p className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-md px-3 py-2" data-testid="failed-sites-notice">
      Could not load: {listNames(sites)}. Their items are not shown — press Refresh to try again.
    </p>
  ) : null;

export const LoadingSitesNotice = ({ sites }) =>
  sites?.length ? (
    <p className="text-xs text-muted-foreground border border-border bg-secondary/40 rounded-md px-3 py-2" data-testid="loading-sites-notice">
      Still loading from {listNames(sites)}… their items will appear automatically.
    </p>
  ) : null;

// Quietly re-runs `reload` every few seconds while some stores are still loading.
export function usePollWhileLoading(loadingSites, reload) {
  const active = (loadingSites?.length || 0) > 0;
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(reload, 5000);
    return () => clearTimeout(t);
  }, [active, loadingSites, reload]);
}
